import config from '@/config';
import type { Viewer } from '@/core/viewer';

/**
 * The expert-payments console gate, as a pure predicate.
 *
 * Split out of `featureFlag.ts` on the `consulting-engagements/gate.ts`
 * precedent, so a caller that already holds `viewer` — the admin nav does —
 * can check it without pulling the root store in.
 *
 * This is only the *visibility* half, and deliberately the weaker one. Three
 * gates guard this surface and they fail differently:
 *
 * 1. **Feature** — this predicate. A dark environment 404s.
 * 2. **Reach** — `superAdminRequiredLegacy` on the route, which asks the api
 *    `permission.allowed('profile', 'super_admin', viewer.id)`. `viewer.admin`
 *    below is the client-side echo of that, not a substitute for it: the route
 *    middleware is the real check and it runs server-side.
 * 3. **Move money** — the api's own settle-role deny on
 *    pay/record_manual/resolve, which no client flag can influence.
 *
 * So a viewer who passes this can settle: the super-admin tier and the
 * settle-role tier are the same staff set today, which is why no action on the
 * console renders in a permanently-disabled state. The gap that leaves —
 * Fulfillment staff who may watch the queue and void a bad row in the api but
 * have no UI for it — is filed as a follow-up; closing it needs an api
 * viewer-capability field, because the webapp session carries no
 * admin/owner-vs-member signal at all (`core/session.ts`).
 */
export function isExpertPaymentsConsoleEnabled(viewer: Viewer): boolean {
  if (!config.enableExpertPaymentsConsole) return false;
  return viewer.admin === true;
}
